/**
 * AuthContext — single source of truth for the current user + settings.
 *
 * Wraps the app in a provider that:
 *   1. On mount, calls GET /api/auth/me to hydrate `user` + `settings`
 *      (and sets the XSRF-TOKEN cookie on first visit).
 *   2. Exposes `login`, `logout`, `requestSignup`, `refresh`,
 *      `updateProfile`, `updateSettings`, `uploadAvatar`, `deleteAvatar`.
 *      (Signup is admin-approved: `requestSignup` submits an application,
 *      the applicant later gets an email with a set-password link.)
 *   3. Exposes `loading` (initial hydration) and `authError` state.
 *
 * Usage:
 *   const { user, settings, loading, login, logout } = useAuth();
 *   if (loading) return null;
 *   if (!user) return <LogInPrompt />;
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { api, apiFetch, ApiError } from '../api/client';

const AuthContext = createContext(null);

const EMPTY_SETTINGS = {};

function normalizeSettings(raw) {
  return raw && typeof raw === 'object' ? raw : EMPTY_SETTINGS;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const applyPayload = useCallback(data => {
    const nextUser = data && data.user ? data.user : null;
    setUser(nextUser);
    setSettings(
      nextUser ? normalizeSettings(data && data.settings) : EMPTY_SETTINGS,
    );
    return nextUser;
  }, []);

  const refresh = useCallback(async () => {
    try {
      const data = await api.get('/api/auth/me');
      applyPayload(data);
      setAuthError(null);
      return data && data.user ? data.user : null;
    } catch (e) {
      // /api/auth/me should never 4xx. Network errors leave the previous
      // user state intact rather than logging the user out spuriously.
      if (e instanceof ApiError && e.code === 'network_error') {
        setAuthError(e);
      } else {
        setUser(null);
        setSettings(EMPTY_SETTINGS);
        setAuthError(e);
      }
      return null;
    }
  }, [applyPayload]);

  useEffect(() => {
    let alive = true;
    (async () => {
      await refresh();
      if (alive) setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [refresh]);

  const login = useCallback(
    async (username, password, remember) => {
      const data = await api.post('/api/auth/login', {
        username,
        password,
        remember: !!remember,
      });
      const next = applyPayload(data);
      setAuthError(null);
      return next;
    },
    [applyPayload],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout', {});
    } catch (e) {
      // Even if the request fails, clear the client-side state.
    }
    setUser(null);
    setSettings(EMPTY_SETTINGS);
    setAuthError(null);
  }, []);

  /**
   * Public POST /api/auth/request-signup with { username, email,
   * display_name?, reason? }. Server always returns { ok: true } to avoid
   * enumeration; the applicant will get a password-set email once an admin
   * approves. No session change here.
   */
  const requestSignup = useCallback(async payload => {
    return api.post('/api/auth/request-signup', payload);
  }, []);

  /**
   * PATCH /api/profile with { display_name?, email? }. Server returns the
   * fresh user + settings, which we drop into state.
   */
  const updateProfile = useCallback(
    async patch => {
      const data = await api.patch('/api/profile', patch);
      applyPayload(data);
      return data;
    },
    [applyPayload],
  );

  /**
   * PUT /api/settings with the full settings object. Server sanitizes and
   * returns the persisted version, which becomes our new local state.
   */
  const updateSettings = useCallback(
    async patch => {
      const merged = { ...settings, ...patch };
      const data = await api.put('/api/settings', merged);
      const next = normalizeSettings(data && data.settings);
      setSettings(next);
      return next;
    },
    [settings],
  );

  /**
   * POST multipart /api/profile/avatar with a Blob/File under the `avatar`
   * field. `apiFetch` skips JSON serialization when `body` is FormData.
   */
  const uploadAvatar = useCallback(
    async blob => {
      const form = new FormData();
      // Provide a filename so PHP populates $_FILES['avatar'] correctly.
      let filename = 'avatar.jpg';
      if (blob && blob.name) {
        filename = blob.name;
      } else if (blob && blob.type === 'image/png') {
        filename = 'avatar.png';
      } else if (blob && blob.type === 'image/webp') {
        filename = 'avatar.webp';
      }
      form.append('avatar', blob, filename);
      const data = await apiFetch('/api/profile/avatar', {
        method: 'POST',
        body: form,
      });
      applyPayload(data);
      return data;
    },
    [applyPayload],
  );

  const deleteAvatar = useCallback(async () => {
    const data = await api.del('/api/profile/avatar');
    applyPayload(data);
    return data;
  }, [applyPayload]);

  /**
   * Confirm a signup or email-change link. Selector + token come from the
   * `?verify=SELECTOR.TOKEN` query param the SPA extracts on mount.
   */
  const verifyEmail = useCallback(
    async (selector, token) => {
      const data = await api.post('/api/auth/verify-email', {
        selector,
        token,
      });
      applyPayload(data);
      return data;
    },
    [applyPayload],
  );

  /**
   * Ask the server to re-send the current user's verification email.
   * Silently succeeds when the email is already verified or mail is disabled.
   */
  const resendVerification = useCallback(async () => {
    return api.post('/api/auth/resend-verification', {});
  }, []);

  /**
   * Trigger the password-reset flow. Server always returns {ok:true} to
   * avoid account enumeration.
   */
  const forgotPassword = useCallback(async email => {
    return api.post('/api/auth/forgot-password', { email });
  }, []);

  /**
   * Complete a password reset. Selector + token come from the
   * `?reset=SELECTOR.TOKEN` link in the email. Signs the user in on success.
   */
  const resetPassword = useCallback(
    async (selector, token, password) => {
      const data = await api.post('/api/auth/reset-password', {
        selector,
        token,
        password,
      });
      applyPayload(data);
      return data;
    },
    [applyPayload],
  );

  const value = useMemo(
    () => ({
      user,
      settings,
      loading,
      authError,
      login,
      logout,
      requestSignup,
      refresh,
      updateProfile,
      updateSettings,
      uploadAvatar,
      deleteAvatar,
      verifyEmail,
      resendVerification,
      forgotPassword,
      resetPassword,
    }),
    [
      user,
      settings,
      loading,
      authError,
      login,
      logout,
      requestSignup,
      refresh,
      updateProfile,
      updateSettings,
      uploadAvatar,
      deleteAvatar,
      verifyEmail,
      resendVerification,
      forgotPassword,
      resetPassword,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an <AuthProvider>');
  }
  return ctx;
}
